import {
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from "react";
// import "../assets/css/global.css";
import LayoutContainer from "../LayoutContainer";
import { AuthContext } from "../../contexts/auth-context";
import { ColorContext } from "../../contexts/color-context/ColorContext";
import { useAsyncFn } from "../../hooks/use-async-fn";

import TableShimmer from "../TableShimmer";

import { FormContext } from "../../contexts/form-context";
import InforIcon from "../../assets/InfoCircleIcon";
import { MdBarChart, MdSearch } from "../../onam/icons"; // ONAM glyphs (was react-icons/md)
import { Tooltip } from "../Tooltip";
import NoSchemaComponent from "../svg-component/noSchema";
import CheckIconSVGComponent from "../svg-component/checkIcon";
import styles from "./identitiesAttributes.module.css";
import { useSidebar } from "../NewSideBar/SidebarContext";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import PageNotFound from "../PageNotFound";
import { useFederation } from "../FederationSelection/FederationContext";
import InfoPopup from "../InfoPopup";
import { formatDateTime } from "../../utils";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";
const AttributeDistributionModal = lazy(
  () => import("../AttributeDistributionModal"),
);

const IdentitiesAttributes = () => {
  const {
    getGlobalSchema,
    getGlobalSchemawithFilters,
    getApplication,
    getFednDetails,
    getLlmModels,
    getAttrDistribution,
  } = useContext(AuthContext);
  const [buckets, setBuckets] = useState([]);
  const history = useHistory();
  const location = useLocation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchParams = new URLSearchParams(location.search);
  const clearedRef = useRef(false);
  const { setBreadcrumbItems } = useBreadcrumb();
  const {
    mainMenuItems,
    activeSubItem,
    setActiveSubItem,
    setIsSideBarMenuDisabled,
  } = useSidebar();
  const colorContext = useContext(ColorContext);
  let sessionFederationId = localStorage.getItem("federationkey");
  const storedPermissions = JSON.parse(localStorage.getItem("userPermissions"));
  const fullAccess = storedPermissions?.query === "full";
  const { branding } = useFederation();
  const getApplicationFn = useAsyncFn({
    promiseFn: getApplication,
  });

  const getFednDetailsFn = useAsyncFn({
    deferFn: getFednDetails,
  });

  const getLlmModelsFn = useAsyncFn({
    deferFn: getLlmModels,
  });
  const [hasLlmModels, setHasLlmModels] = useState<boolean | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sessionFederationId)
      getFednDetailsFn
        .run({
          federationID: sessionFederationId,
        })
        .catch((err) => {
          console.error("Handled gracefully:", err);
        });
  }, [sessionFederationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hanldeDiscoverAI = () => {
    history.push("/ask-tamarai");
  };

  const [distributionLoading, setDistributionLoading] = useState(false);
  const [distributionError, setDistributionError] = useState<string | null>(
    null,
  );

  const handleAttrDistribution = async () => {
    setDistributionLoading(true);
    setDistributionError(null);
    setDistributionData([]);
    try {
      const result = await getAttrDistribution({
        args: {
          federationId: sessionFederationId,
          organizationId: localStorage.getItem("organizationkey"),
        },
      });
      if (result?.message && (!Array.isArray(result) || result.length === 0)) {
        setDistributionError(result.message);
        setDistributionData([]);
      } else {
        // Distribution API has no table name. Keep raw rows; modal resolves
        // org/table/created from the page's buckets (same source as matrix tooltips).
        setDistributionData(Array.isArray(result) ? result : []);
      }
    } catch (err: any) {
      const raw = err?.response?.data?.message || err?.message;
      let displayMsg = "Something went wrong. Please try again.";
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          displayMsg = parsed?.message || parsed?.error || displayMsg;
        } catch {
          displayMsg = raw;
        }
      }
      setDistributionError(displayMsg);
      setDistributionData([]);
    } finally {
      setDistributionLoading(false);
    }
  };

  const [showTooltip, setShowTooltip] = useState(false);
  const [showTamaraiTooltip, setShowTamaraiTooltip] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [attributeSearch, setAttributeSearch] = useState("");
  const [showAttrSearch, setShowAttrSearch] = useState(false);
  const [secondaryColorCode, setSecondaryColorCode] = useState("");
  const [elementColorCode, setElementColorCode] = useState("");
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [distributionData, setDistributionData] = useState<any[] | null>(null);
  const [selectedBucketIds, setSelectedBucketIds] = useState([]);
  const DEFAULT_COL_WIDTH = 140;
  const [colWidths, setColWidths] = useState<Record<number, number>>({});
  const [hoveredColIndex, setHoveredColIndex] = useState<number | null>(null);
  const [rowTooltipInfo, setRowTooltipInfo] = useState<{
    x: number;
    y: number;
    name: string;
    identifier_type: string | null;
  } | null>(null);
  const [bucketTooltipInfo, setBucketTooltipInfo] = useState<{
    x: number;
    y: number;
    bucket: any;
  } | null>(null);
  const [tickTooltipInfo, setTickTooltipInfo] = useState<{
    x: number;
    y: number;
    values: (string | boolean | number)[] | null;
  } | null>(null);
  const dragRef = useRef<{
    colIndex: number;
    startX: number;
    startWidth: number;
    col: HTMLElement;
  } | null>(null);

  const startResize = (colIndex: number, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget as HTMLElement;
    const table = handle.closest("table") as HTMLTableElement | null;
    if (!table) return;

    const cols = table.querySelectorAll<HTMLElement>("colgroup col");
    const col = cols[colIndex];
    if (!col) return;

    // Snapshot every th's actual rendered width and lock all cols to those sizes.
    // This prevents other columns from jumping to DEFAULT_COL_WIDTH on commit.
    const ths = table.querySelectorAll<HTMLElement>("thead tr th");
    const allWidths: Record<number, number> = {};
    ths.forEach((thEl, i) => {
      allWidths[i] = thEl.getBoundingClientRect().width;
      if (cols[i]) cols[i].style.width = `${allWidths[i]}px`;
    });

    const startWidth = allWidths[colIndex] ?? DEFAULT_COL_WIDTH;
    const startTableWidth = table.getBoundingClientRect().width;
    // Give the table an explicit pixel width so it can expand beyond min-width: 100%
    table.style.width = `${startTableWidth}px`;
    table.classList.add(styles.tableDragging);

    dragRef.current = { colIndex, startX: e.clientX, startWidth, col };

    const onMove = (moveE: PointerEvent) => {
      moveE.preventDefault();
      if (!dragRef.current) return;
      const delta = moveE.clientX - dragRef.current.startX;
      const newColWidth = Math.max(80, dragRef.current.startWidth + delta);
      dragRef.current.col.style.width = `${newColWidth}px`;
      table.style.width = `${startTableWidth + (newColWidth - dragRef.current.startWidth)}px`;
    };

    const onUp = () => {
      if (dragRef.current) {
        const finalColWidth =
          parseFloat(dragRef.current.col.style.width) ||
          dragRef.current.startWidth;
        const finalWidths = {
          ...allWidths,
          [dragRef.current.colIndex]: finalColWidth,
        };
        setColWidths(finalWidths);
        table.style.width = "";
        table.classList.remove(styles.tableDragging);
        dragRef.current = null;
      }
      window.removeEventListener("pointermove", onMove, { capture: true });
      window.removeEventListener("pointerup", onUp, { capture: true });
    };

    window.addEventListener("pointermove", onMove, { capture: true });
    window.addEventListener("pointerup", onUp, { capture: true });
  };

  const {
    setColor,
    setsecondaryfedColor,
    setFedPlatformName,
    setFedPlatformLogo,
    setelementfedColor,
    setMenuItemNames,
  } = colorContext;

  const [, setMenuItem] = useState([]);

  const fedSpecificMenuLabels = JSON.parse(
    localStorage.getItem("fedSpecificMenuLabels") || "{}",
  );

  useEffect(() => {
    if (selectedBucketIds.length) {
      localStorage.setItem("bucketids", JSON.stringify(selectedBucketIds));
      const selectedBucketObjects = buckets.filter((b) =>
        selectedBucketIds.includes(b.bucket_id),
      );
      localStorage.setItem(
        "selectedBucketObjects",
        JSON.stringify(selectedBucketObjects),
      );
    } else {
      localStorage.setItem("bucketids", JSON.stringify([]));
      localStorage.setItem("selectedBucketObjects", JSON.stringify([]));
    }
  }, [selectedBucketIds, buckets]);

  useEffect(() => {
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/campaign-planning")),
      )?.label;

    if (activeSubItem !== sideBarMenuToHighlight) {
      setActiveSubItem(sideBarMenuToHighlight);
    }

    setIsSideBarMenuDisabled({
      fed: false,
      cp: false,
      cr: false,
      pr: false,
      ah: false,
      ca: false,
      dm: false,
    });
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Federation Selection", path: "/federation-selection" },
      {
        label: fedSpecificMenuLabels?.campaign_planning
          ? fedSpecificMenuLabels.campaign_planning
          : "Audience Curation",
        path: "/campaign-planning",
      },
    ]);

    const fetchLlmModels = async () => {
      try {
        const orgId = localStorage.getItem("organizationkey");
        const result = await getLlmModelsFn.run({ organizationId: orgId });
        const hasProviders = Array.isArray(result?.providers)
          ? result.providers.length > 0
          : false;
        setHasLlmModels(hasProviders);
      } catch {
        setHasLlmModels(false);
      }
    };
    fetchLlmModels();

    return () => {
      ClearFilters();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (buckets.length === 1) {
      setSelectedBucketIds([buckets[0].bucket_id]);
    }
  }, [buckets]);

  const applicationData = useMemo(
    () => getApplicationFn.data,
    [getApplicationFn.data],
  );
  const fedData = useMemo(() => getFednDetailsFn.data, [getFednDetailsFn.data]);

  useEffect(() => {
    const applicationData = getApplicationFn.data && getApplicationFn.data;
    const fedData = getFednDetailsFn.data && getFednDetailsFn.data;

    if (
      getApplicationFn.data &&
      applicationData &&
      applicationData.color_scheme
    ) {
      if (
        applicationData?.setting?.toLowerCase() === "federation settings" &&
        getFednDetailsFn.data &&
        fedData
      ) {
        setSecondaryColorCode(branding?.secondary_color);
        setElementColorCode(branding?.element_color);
        localStorage.setItem(
          "fedPrimaryColor",
          fedData.color_scheme.primary_color,
        );
        localStorage.setItem(
          "fedSecondaryColor",
          fedData.color_scheme.secondary_color,
        );
        localStorage.setItem(
          "fedElementColor",
          fedData.color_scheme.element_color,
        );
      } else {
        setSecondaryColorCode(applicationData.color_scheme.secondary_color);
        setElementColorCode(applicationData.color_scheme.element_color);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationData, fedData]);

  useEffect(() => {
    const fedData = getFednDetailsFn.data && getFednDetailsFn.data;

    if (getFednDetailsFn.data && fedData && fedData.color_scheme) {
      setColor(fedData.color_scheme.primary_color);
      setsecondaryfedColor(fedData.color_scheme.secondary_color);
      setFedPlatformLogo(fedData.platform_logo);
      setelementfedColor(fedData.color_scheme.element_color);
      setMenuItemNames(fedData.menu_items);
      setMenuItem(fedData.menu_items);
    }
  }, [
    setColor,
    setsecondaryfedColor,
    setelementfedColor,
    setMenuItemNames,
    setFedPlatformName,
    setFedPlatformLogo,
    getFednDetailsFn.data,
  ]);

  const NoDataComponent = (props) => {
    return (
      <div className={styles.emptycontainer}>
        <NoSchemaComponent
          primaryColor={elementColorCode}
          secondaryColor={secondaryColorCode}
        />
        <div className={styles.emptytext}>ID Type not selected.</div>
        <div className={styles.emptysubtitle}>
          Select a ID Type above to view dataset attributes.
        </div>
      </div>
    );
  };

  const { fields, resetFormContext, setAndValidateFieldValue } =
    useContext(FormContext);

  const handleCheckboxChange = (bucket) => {
    const bucketId = bucket.bucket_id;

    setSelectedBucketIds((prevSelected) =>
      prevSelected.includes(bucketId)
        ? prevSelected.filter((bucket_id) => bucket_id !== bucketId)
        : [...prevSelected, bucketId],
    );
  };

  const usermatchid = fields["selectMatchId"];

  var userMatchId;
  if (usermatchid !== undefined && usermatchid.length > 0) {
    userMatchId = usermatchid[0].value;
    localStorage.setItem("userMatchId", userMatchId);
  }

  const getGlobalSchemaFn = useAsyncFn({
    promiseFn: getGlobalSchema,
    federationId: sessionFederationId,
    organizationId: localStorage.getItem("organizationkey"),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ClearFilters = () => {
    resetFormContext();
    setSelectedBucketIds([]);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchParams.has("init") && !clearedRef.current) {
      ClearFilters();
      clearedRef.current = true;
    }
  }, [searchParams, ClearFilters]);

  useEffect(() => {
    const ids = getGlobalSchemaFn.data?.canonical_identity_types;
    if (ids?.length > 0 && !fields["selectMatchId"]?.length) {
      setAndValidateFieldValue("selectMatchId", [
        { value: ids[0].key, label: ids[0].value },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getGlobalSchemaFn.data]);

  const getglobalschemafiltersfn = useAsyncFn({
    deferFn: getGlobalSchemawithFilters,
    federationId: sessionFederationId,
    identifier: "",
    canonical_identity_type: userMatchId,
  });

  var data;
  if (userMatchId !== undefined) {
    data = getglobalschemafiltersfn.data || getGlobalSchemaFn.data;
  } else {
    data = getGlobalSchemaFn.data && getGlobalSchemaFn.data;
  }

  useEffect(() => {
    if (userMatchId) {
      localStorage.setItem("matchid", usermatchid[0].value);
      getglobalschemafiltersfn.run({
        federationId: sessionFederationId,
        identifier: "",
        canonical_identity_type: userMatchId,
        organizationId: localStorage.getItem("organizationkey"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMatchId]);

  useEffect(() => {
    setSelectedBucketIds([]);
    setBuckets([]);
    setColWidths({});
    localStorage.setItem("bucketids", JSON.stringify([]));
    localStorage.setItem("selectedBucketObjects", JSON.stringify([]));
  }, [userMatchId]);

  var userBucketId = fields["selectBucket"];

  useEffect(() => {
    if (!userMatchId && !userBucketId && buckets?.length !== 0) {
      setBuckets([]);
    }
  }, [userBucketId, userMatchId, buckets]);

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useEffect(() => {
    if (userBucketId !== undefined) {
      setBuckets(selectedBucket);
    } else if (userMatchId !== undefined && getglobalschemafiltersfn.isFulfilled && data?.buckets) {
      setBuckets(
        data.buckets.filter(
          (b: any) => !b.canonical_identity_type || b.canonical_identity_type === userMatchId,
        ),
      );
    } else if (!userMatchId) {
      setBuckets((prev) => (prev.length !== 0 ? [] : prev));
    }
  }, [userBucketId, userMatchId, selectedBucket, data, getglobalschemafiltersfn.isFulfilled]); // eslint-disable-line react-hooks/exhaustive-deps, @typescript-eslint/no-use-before-define

  if (userMatchId !== undefined && getglobalschemafiltersfn.isFulfilled) {
    data = getglobalschemafiltersfn && getglobalschemafiltersfn.data;
  }

  if (getGlobalSchemaFn.isPending) {
    return (
      <LayoutContainer>
        <div className={styles.wrapperContainer}>
          <div className={styles.queryContainer}>
            <div className={styles.shimmerSelect} />
            <div className={styles.shimmerBtn} />
            <div className={styles.shimmerBtn} />
          </div>
          <TableShimmer rows={8} columnWidths={[3, 2, 2, 2, 2]} />
        </div>
      </LayoutContainer>
    );
  }
  if (!data) {
    return <NoDataComponent />;
  }

  var match_ids;
  var selectedBucket = new Array(1).fill({ key: "value" });
  var global_identifiers: string[] =
    (data as any)?.global_identifiers || [];

  match_ids =
    getGlobalSchemaFn.data?.canonical_identity_types ||
    data.canonical_identity_types ||
    [];

  if (userBucketId !== undefined) {
    for (var k = 0; k < data.buckets.length; k++) {
      for (var j = 0; j < userBucketId.length; j++) {
        if (userBucketId[j].value === data.buckets[k].bucket_id) {
          const obj = data.buckets[k];
          selectedBucket.push(obj);
        }
      }
    }
  }
  selectedBucket.shift();

  const attSet = new Set();

  if (buckets) {
    buckets.forEach((bucket) => {
      Array.isArray(bucket.attributes) &&
        bucket.attributes.forEach((att) => {
          if (
            att.name &&
            att.purpose !== "identifier" &&
            !global_identifiers.includes(att.name)
          ) {
            attSet.add(att.name);
          }
        });
    });
  }

  const attArr = Array.from(attSet);

  const rows = attArr.map((att) => {
    const foundBucket = buckets.find((b) =>
      b.attributes?.some((a: any) => a.name === att),
    );
    const foundAttr = foundBucket?.attributes?.find((a: any) => a.name === att);
    const label = foundAttr?.label ?? null;
    const identifier_type =
      foundAttr?.identifier_type ??
      foundBucket?.identifier_type ??
      (data as any)?.identifier_type ??
      null;

    return buckets.reduce(
      (acc, bucket) => {
        const getBucketKey = (bucket) =>
          `${bucket.organization_name}_${bucket.external_table_name}_${bucket.bucket_id}`;
        const key = getBucketKey(bucket);
        const hasAttr =
          Array.isArray(bucket.attributes) &&
          bucket.attributes.some((attrObj) => attrObj.name === att);
        return {
          ...acc,
          attribute: att,
          label,
          identifier_type,
          [key]: !!hasAttr,
        };
      },
      { attribute: att, label, identifier_type },
    );
  });

  const attributeRowsWithBuckets = rows.map((row) => {
    const attributeRow = { attribute: row.attribute, label: row.label, identifier_type: row.identifier_type };
    buckets.forEach((bucket) => {
      const getBucketKey = (bucket) =>
        `${bucket.organization_name}_${bucket.external_table_name}_${bucket.bucket_id}`;
      const key = getBucketKey(bucket);
      const isPresent =
        Array.isArray(bucket.attributes) &&
        bucket.attributes.some((attrObj) => attrObj.name === row.attribute);
      attributeRow[key] = isPresent;
    });
    return attributeRow;
  });

  const sortedRows = attributeRowsWithBuckets.sort((a, b) => {
    if (
      global_identifiers.includes(a.attribute) &&
      !global_identifiers.includes(b.attribute)
    ) {
      return -1;
    }
    if (
      global_identifiers.includes(b.attribute) &&
      !global_identifiers.includes(a.attribute)
    ) {
      return 1;
    }
    return a.attribute > b.attribute ? 1 : -1;
  });

  const selectedBuckets = buckets.filter((b) =>
    selectedBucketIds.includes(b.bucket_id),
  );
  const hasNonMatchIdAttributes = selectedBuckets.some(
    (bucket) =>
      Array.isArray(bucket.attributes) &&
      bucket.attributes.some((attr) => attr.name !== userMatchId),
  );
  const isDisabled =
    !userMatchId ||
    selectedBucketIds.length === 0 ||
    (selectedBucketIds.length > 0 && !hasNonMatchIdAttributes);
  const isNewQueryDisabled = !userMatchId || selectedBucketIds.length === 0;
  const isAskTamaraiDisabled = isDisabled || hasLlmModels === false;
  const isFormClean = !userMatchId && selectedBucketIds.length === 0;
  const raw = localStorage.getItem("fedSpecificMenuItems") || "";
  const fedSpecificMenuItems = raw ? raw.split(",") : [];

  const sortedBuckets = [...buckets]
    .filter(
      (bucket) =>
        Array.isArray(bucket.attributes) &&
        bucket.attributes.some((attr: any) => attr.purpose !== "identifier"),
    )
    .sort((bucketA, bucketB) =>
      bucketA && bucketB && bucketA.vault_id < bucketB.vault_id ? -1 : 1,
    );

  const getBucketKey = (bucket: any) =>
    `${bucket.organization_name}_${bucket.external_table_name}_${bucket.bucket_id}`;

  const filteredRows = attributeSearch.trim()
    ? sortedRows.filter((row) => {
        const display = (row.label as string)?.trim()
          ? (row.label as string)
          : (row.attribute as string);
        return display.toLowerCase().includes(attributeSearch.trim().toLowerCase());
      })
    : sortedRows;

  return storedPermissions?.campaignplanning === "no" ||
    !fedSpecificMenuItems?.includes("campaign_planning") ? (
    <PageNotFound />
  ) : (
    <LayoutContainer>
      <div className={styles.wrapperContainer}>
        <div className={styles.queryContainer}>
          {/* Dropdown Container */}
          <div className={styles.matchIdContainer}>
            <label className={styles.matchIdLabel}>ID Type:</label>
            <div className={styles.matchIdPillsRow}>
              {match_ids?.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`${styles.matchIdPill}${usermatchid?.[0]?.value === item.key ? ` ${styles.matchIdPillActive}` : ""}`}
                  onClick={() =>
                    setAndValidateFieldValue(
                      "selectMatchId",
                      usermatchid?.[0]?.value === item.key
                        ? []
                        : [{ value: item.key, label: item.value }],
                    )
                  }
                >
                  {item.value}
                </button>
              ))}
              {!match_ids?.length ? (
                <span className={styles.matchIdHint}>
                  No datasets present for this federation
                </span>
              ) : !usermatchid?.[0]?.value && (
                <span className={styles.matchIdHint}>
                  — select one to view datasets
                </span>
              )}
            </div>
          </div>

          {fullAccess && (
            <div className={styles.buttonsWrapper}>
              <div
                className={styles.tooltipContainer}
                onMouseEnter={() => setShowTamaraiTooltip(true)}
                onMouseLeave={() => setShowTamaraiTooltip(false)}
              >
                <span
                  className={`${styles.compaindesign} ${isAskTamaraiDisabled ? styles.disabled : ""}`}
                  onClick={() => { if (!isAskTamaraiDisabled) hanldeDiscoverAI(); }}
                >
                  Ask MITRA
                </span>
                {showTamaraiTooltip && (
                  <div className={styles.tooltip}>
                    <div className={styles.tooltipCaret} />
                    {isAskTamaraiDisabled
                      ? hasLlmModels === false && !isDisabled
                        ? "No AI models configured"
                        : selectedBucketIds.length > 0 && !hasNonMatchIdAttributes
                          ? "No queryable attributes selected"
                          : "Select ID type & dataset first"
                      : "AI-enabled querying — describe your audience in plain language and let MITRA build the query for you"}
                  </div>
                )}
              </div>
              <div
                className={styles.tooltipContainer}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <span
                  className={`${styles.newquery} ${isNewQueryDisabled ? styles.disabled : ""}`}
                  onClick={() => {
                    if (!isNewQueryDisabled)
                      history.push(`/new-query?singleorg=${selectedBucketIds?.length === 1}`);
                  }}
                >
                  New Query
                </span>
                {showTooltip && (
                  <div className={styles.tooltip}>
                    <div className={styles.tooltipCaret} />
                    {isNewQueryDisabled
                      ? "Select ID type & dataset first"
                      : "Manual query builder — apply attribute filters and rules to define your audience"}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Clear Filters Section */}
          <div className={styles.clearall}>
            {isFormClean ? (
              <Tooltip text="Nothing to clear" placement="top">
                <span className={`${styles.buttonText} ${styles.disabled}`}>
                  Clear All
                </span>
              </Tooltip>
            ) : showClearConfirm ? (
              <span className={styles.clearConfirmInline}>
                Clear all?&nbsp;
                <span
                  className={styles.clearConfirmYes}
                  onClick={() => { ClearFilters(); setShowClearConfirm(false); }}
                >
                  Yes
                </span>
                &nbsp;·&nbsp;
                <span
                  className={styles.clearConfirmNo}
                  onClick={() => setShowClearConfirm(false)}
                >
                  Cancel
                </span>
              </span>
            ) : (
              <span className={styles.buttonText} onClick={() => setShowClearConfirm(true)}>
                Clear All
              </span>
            )}
            <div className={styles.divider} />
            <Tooltip text="Attribute Distribution" placement="top">
              <span
                className={styles.distributionIconBtn}
                onClick={handleAttrDistribution}
              >
                <MdBarChart size={14} />
                Attribute Distribution
              </span>
            </Tooltip>
            <InforIcon
              className={styles.infoIcon}
              onClick={() => setShowInfoPopup(true)}
            />
            {showInfoPopup && (
              <InfoPopup
                isOpen={showInfoPopup}
                onClose={() => setShowInfoPopup(false)}
                closeButtonLabel="Got it"
                headerText="How it works"
              >
                <div className={styles.infoPopupContent}>
                  <div className={styles.infoPopupStep}>
                    <span className={styles.infoPopupStepNum}>1</span>
                    <div>
                      <strong>Choose an ID type</strong>
                      <p>Select the identity type (e.g. email, phone) that links records across your data partners.</p>
                    </div>
                  </div>
                  <div className={styles.infoPopupStep}>
                    <span className={styles.infoPopupStepNum}>2</span>
                    <div>
                      <strong>Select datasets</strong>
                      <p>Check one or more data partner datasets. The table shows which attributes each dataset contributes.</p>
                    </div>
                  </div>
                  <div className={styles.infoPopupStep}>
                    <span className={styles.infoPopupStepNum}>3</span>
                    <div>
                      <strong>Build your query</strong>
                      <p>Choose how you want to query:</p>
                      <ul className={styles.infoPopupList}>
                        <li><strong>New Query</strong> — manual query builder. Apply attribute filters and rules yourself to define your audience.</li>
                        <li><strong>Ask MITRA</strong> — AI-enabled querying. Describe your audience in plain language and MITRA builds the query for you.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </InfoPopup>
            )}
          </div>
        </div>

        {/* Table Container */}
        <div className={styles.tableWrapper}>
          {getglobalschemafiltersfn.isPending ? (
            <TableShimmer rows={8} columnWidths={[3, 2, 2, 2, 2]} />
          ) : sortedRows.length > 0 ? (
            <table className={styles.table}>
              <colgroup>
                <col
                  style={{ width: `${colWidths[0] ?? DEFAULT_COL_WIDTH}px` }}
                />
                {sortedBuckets.map((bucket, i) => (
                  <col
                    key={bucket.bucket_id}
                    style={{
                      width: `${colWidths[i + 1] ?? DEFAULT_COL_WIDTH}px`,
                    }}
                  />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className={styles.firstTh}>
                    <div className={styles.headerContent}>
                      {showAttrSearch ? (
                        <div className={styles.attrSearchWrapper}>
                          <input
                            autoFocus
                            type="text"
                            className={styles.attrSearchInput}
                            placeholder="Search attributes…"
                            value={attributeSearch}
                            onChange={(e) => setAttributeSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") {
                                setAttributeSearch("");
                                setShowAttrSearch(false);
                              }
                            }}
                          />
                          <button
                            className={styles.attrSearchClear}
                            onClick={() => { setAttributeSearch(""); setShowAttrSearch(false); }}
                            aria-label="Close search"
                          >
                            {(FEATURES.curationSkin ? <OI n="close" size="1em" /> : "×")}
                          </button>
                        </div>
                      ) : (
                        <>
                          <span>Dataset Attributes</span>
                          <button
                            className={styles.headerSearchIcon}
                            onClick={() => setShowAttrSearch(true)}
                            aria-label="Search attributes"
                          >
                            <MdSearch size={13} />
                          </button>
                        </>
                      )}
                    </div>
                    <div
                      className={styles.resizeHandle}
                      onPointerDown={(e) => startResize(0, e)}
                    />
                  </th>
                  {sortedBuckets.map((bucket, i) => {
                    return (
                      <th
                        key={bucket.bucket_id}
                        className={`${selectedBucketIds.includes(bucket.bucket_id) ? "is-picked" : ""}${hoveredColIndex === i ? " is-hover" : ""}` || undefined}
                        onMouseEnter={(e) => {
                          setHoveredColIndex(i);
                          const rect = e.currentTarget.getBoundingClientRect();
                          setBucketTooltipInfo({
                            x: Math.min(
                              rect.left + rect.width / 2,
                              window.innerWidth - 175,
                            ),
                            y: rect.bottom,
                            bucket,
                          });
                        }}
                        onMouseLeave={() => { setBucketTooltipInfo(null); setHoveredColIndex(null); }}
                      >
                        <div className={styles.headerContent}>
                          <input
                            type="checkbox"
                            checked={selectedBucketIds.includes(
                              bucket.bucket_id,
                            )}
                            onChange={() => handleCheckboxChange(bucket)}
                            className={styles.checkbox}
                          />
                          <span className={styles.bucketOrgName}>
                            {bucket.organization_name}
                          </span>
                        </div>
                        <div
                          className={styles.resizeHandle}
                          onPointerDown={(e) => startResize(i + 1, e)}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const { attribute, label, identifier_type } = row;
                  const display = label?.trim() ? label : attribute;
                  return (
                    <tr
                      key={attribute}
                      onMouseLeave={() => setHoveredColIndex(null)}
                    >
                      <td
                        onMouseEnter={(e) => {
                          const rect =
                            e.currentTarget.getBoundingClientRect();
                          setRowTooltipInfo({
                            x: rect.left,
                            y: rect.top,
                            name: attribute,
                            identifier_type: identifier_type ?? null,
                          });
                        }}
                        onMouseLeave={() => setRowTooltipInfo(null)}
                      >
                        <div className={styles.firstTd}>{display}</div>
                      </td>
                      {sortedBuckets.map((bucket, colIdx) => {
                        const key = getBucketKey(bucket);
                        return (
                          <td
                            key={key}
                            // "is-picked": the dataset is ticked in its header; the ONAM skin tints its column
                            className={`${hoveredColIndex === colIdx ? styles.colHovered : ""}${selectedBucketIds.includes(bucket.bucket_id) ? " is-picked" : ""}`}
                            onMouseEnter={() => setHoveredColIndex(colIdx)}
                            onMouseLeave={() => {
                              setHoveredColIndex(null);
                              setTickTooltipInfo(null);
                            }}
                          >
                            {row?.[key] ? (
                              <div
                                className={styles.checkIconContainer}
                                onMouseEnter={(e) => {
                                  const attrValues =
                                    bucket.attributes?.find(
                                      (a: any) => a.name === row.attribute,
                                    )?.values ?? null;
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTickTooltipInfo({
                                    x: rect.left + rect.width / 2,
                                    y: rect.top,
                                    values: attrValues,
                                  });
                                }}
                                onMouseLeave={() => setTickTooltipInfo(null)}
                              >
                                <CheckIconSVGComponent
                                  primaryColor={elementColorCode}
                                  style={{
                                    width: "18.74px",
                                    height: "13.077px",
                                  }}
                                />
                              </div>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <NoDataComponent />
          )}
        </div>
      </div>

      {/* {rowTooltipInfo && (
        <div
          className={styles.fixedTooltip}
          style={{ top: Math.max(8, rowTooltipInfo.y - 60), left: rowTooltipInfo.x }}
        >
          <div className={styles.tooltipLine}>
            <span className={styles.tooltipLabel}>Name:</span>{" "}
            {rowTooltipInfo.name}
          </div>
          {rowTooltipInfo.identifier_type && (
            <div className={styles.tooltipLine}>
              <span className={styles.tooltipLabel}>Identifier:</span>{" "}
              {rowTooltipInfo.identifier_type}
            </div>
          )}
        </div>
      )} */}

      {bucketTooltipInfo && (
        <div
          className={`${styles.fixedTooltip} ${styles.fixedBucketTooltip}`}
          style={{
            top: bucketTooltipInfo.y + 10,
            left: bucketTooltipInfo.x,
            transform: "translateX(-50%)",
          }}
        >
          {bucketTooltipInfo.bucket.external_table_name && (
            <div className={styles.tooltipLine}>
              <span className={styles.tooltipLabel}>Table:</span>{" "}
              {bucketTooltipInfo.bucket.external_table_name}
            </div>
          )}
          {bucketTooltipInfo.bucket.bucket_name && (
            <div className={styles.tooltipLine}>
              <span className={styles.tooltipLabel}>Dataset Name:</span>{" "}
              {bucketTooltipInfo.bucket.bucket_name}
            </div>
          )}
          {(bucketTooltipInfo.bucket.identifier_type ??
            bucketTooltipInfo.bucket.canonical_identity_type) && (
            <div className={styles.tooltipLine}>
              <span className={styles.tooltipLabel}>Identifier:</span>{" "}
              {bucketTooltipInfo.bucket.identifier}
            </div>
          )}
          {bucketTooltipInfo.bucket.created_at && (
            <div className={styles.tooltipLine}>
              <span className={styles.tooltipLabel}>Created At:</span>{" "}
              {formatDateTime(bucketTooltipInfo.bucket.created_at)}
            </div>
          )}
        </div>
      )}

      {tickTooltipInfo && (
        <div
          className={styles.tickTooltip}
          style={{
            top: tickTooltipInfo.y - 8,
            left: tickTooltipInfo.x,
            transform: "translateX(-50%) translateY(-100%)",
          }}
        >
          <div className={styles.tooltipLine}>
            <span className={styles.tooltipLabel}>Values:</span>
          </div>
          {tickTooltipInfo.values?.length ? (
            <div className={styles.tickTooltipValues}>
              {tickTooltipInfo.values.slice(0, 15).map((v, i) => (
                <span key={i} className={styles.tickTooltipValue}>
                  {v != null ? String(v) : "—"}
                </span>
              ))}
              {tickTooltipInfo.values.length > 15 && (
                <span className={styles.tickTooltipMore}>
                  +{tickTooltipInfo.values.length - 15} more
                </span>
              )}
            </div>
          ) : (
            <div className={styles.tickTooltipEmpty}>No values available</div>
          )}
        </div>
      )}

      {distributionData !== null && (
        <Suspense fallback={null}>
          <AttributeDistributionModal
            data={distributionData}
            buckets={
              (getGlobalSchemaFn.data as any)?.buckets ||
              (data as any)?.buckets ||
              buckets
            }
            loading={distributionLoading}
            error={distributionError}
            onClose={() => {
              setDistributionData(null);
              setDistributionLoading(false);
              setDistributionError(null);
            }}
          />
        </Suspense>
      )}

    </LayoutContainer>
  );
};

export default IdentitiesAttributes;
